import { create } from "zustand";
import {
  getMyProfile,
  updateMyProfile,
  initiateKyc,
  requestAuthChallenge,
  loginWithWallet as loginWithWalletApi,
  registerWithWallet,
  connectWithWallet as connectWithWalletApi,
  getWalletSession,
  logoutWalletSession,
  startXAuth,
  startDiscordAuth,
  disconnectX,
  disconnectDiscord,
  requestAvatarUploadUrl,
  completeAvatarUpload as completeAvatarUploadApi,
  deleteAvatar as deleteAvatarApi,
} from "@lib/apiClients";
import { PrivateUserProfileView, UserProfileUpdate } from "@bbb/model";
import { queryKeys, queryClient } from "@lib/queryClient";
import {
  identifyAnalyticsUser,
  resetAnalyticsUser,
  trackUsageEvent,
} from "@lib/analytics/posthog";
import { AuthState } from "@interfaces";

function buildAuthUser(profile: PrivateUserProfileView) {
  const address = profile.authWalletAddress ?? profile.id;
  return {
    uid: profile.id,
    address,
    reload: async () => undefined,
  };
}

function cacheUserProfile(nextUser: PrivateUserProfileView) {
  queryClient.setQueryData(queryKeys.userProfile(nextUser.id), nextUser);
}

export async function fetchUserProfile(): Promise<PrivateUserProfileView> {
  return getMyProfile();
}

function mapUserUpdatesToPayload(
  updates: Partial<PrivateUserProfileView>,
): UserProfileUpdate {
  if (!updates.id) {
    throw new Error("Cannot update profile without an id");
  }

  const payload: UserProfileUpdate = { id: updates.id };
  if (typeof updates.displayName === "string" && updates.displayName.trim()) {
    payload.displayName = updates.displayName.trim();
  }
  if (updates.bio !== undefined) payload.bio = updates.bio;
  if (updates.location !== undefined) payload.location = updates.location;
  if (updates.privacy) payload.privacy = { ...updates.privacy };
  if (updates.userType) payload.userType = updates.userType;
  if (updates.xAccount !== undefined) payload.xAccount = updates.xAccount;

  return payload;
}

function getCurrentReturnTo() {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

async function hydrateSession(set: (partial: Partial<AuthState>) => void) {
  const session = await getWalletSession();
  if (!session.authenticated || !session.profile) {
    set({
      authUser: null,
      isAuthenticated: false,
      isAuthReady: true,
      lastVerificationEmailSentAt: null,
    });
    queryClient.removeQueries({ queryKey: queryKeys.userProfileRoot });
    return null;
  }

  cacheUserProfile(session.profile);
  set({
    authUser: buildAuthUser(session.profile),
    isAuthenticated: true,
    isAuthReady: true,
    lastVerificationEmailSentAt: null,
  });
  identifyAnalyticsUser(session.profile, {
    auth_provider: "wallet",
  });
  return session.profile;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  isAuthenticated: false,
  isAuthReady: false,
  lastVerificationEmailSentAt: null,
  authUser: null,

  initialize: () => {
    void hydrateSession(set).catch((error) => {
      console.error("Failed to hydrate wallet session:", error);
      set({
        authUser: null,
        isAuthenticated: false,
        isAuthReady: true,
        lastVerificationEmailSentAt: null,
      });
    });
    return () => undefined;
  },

  getSessionAuthToken: async () => undefined,

  connectWithWallet: async ({ address, signMessage }) => {
    const domain =
      typeof window !== "undefined" ? window.location.origin : "infinica";
    const challenge = await requestAuthChallenge({ address, domain });
    const signature = await signMessage(challenge.message);
    const result = await connectWithWalletApi({
      address,
      domain,
      nonce: challenge.nonce,
      signature,
    });

    cacheUserProfile(result.profile);
    set({
      authUser: buildAuthUser(result.profile),
      isAuthenticated: true,
      isAuthReady: true,
    });
    identifyAnalyticsUser(result.profile, {
      auth_provider: "wallet",
    });
    trackUsageEvent("wallet_connected", {
      method: "wallet",
      created_profile: result.createdProfile === true,
    });
  },

  loginWithWallet: async ({ address, signMessage, mode }) => {
    const domain =
      typeof window !== "undefined" ? window.location.origin : "infinica";
    const challenge = await requestAuthChallenge({ address, domain });
    const signature = await signMessage(challenge.message);
    const result =
      mode === "register"
        ? await registerWithWallet({
            address,
            domain,
            nonce: challenge.nonce,
            signature,
          })
        : await loginWithWalletApi({
            address,
            domain,
            nonce: challenge.nonce,
            signature,
          });

    cacheUserProfile(result.profile);
    set({
      authUser: buildAuthUser(result.profile),
      isAuthenticated: true,
      isAuthReady: true,
    });
    identifyAnalyticsUser(result.profile, {
      auth_provider: "wallet",
    });
    trackUsageEvent(mode === "register" ? "user_signed_up" : "user_logged_in", {
      method: "wallet",
    });
  },

  logout: async () => {
    await logoutWalletSession().catch((error) => {
      console.warn("Wallet logout request failed; clearing local state", error);
    });
    resetAnalyticsUser();
    set({
      authUser: null,
      isAuthenticated: false,
      lastVerificationEmailSentAt: null,
    });
    queryClient.removeQueries({ queryKey: queryKeys.userProfileRoot });
  },

  InitKycSession: async () => {
    const result = await initiateKyc();
    trackUsageEvent("kyc_session_started", {
      already_verified: result.alreadyVerified ?? false,
      status: result.status ?? null,
    });
    if (result.alreadyVerified || result.status === "verified") {
      await get().refreshUserProfile();
    }
    return result;
  },

  updateUserProfile: async (updates) => {
    const { authUser } = get();
    if (!authUser) return;

    const queryKey = queryKeys.userProfile(authUser.uid);
    const cachedUser = queryClient.getQueryData<PrivateUserProfileView>(queryKey);
    const baseUser = cachedUser ?? (await fetchUserProfile());
    const payload = mapUserUpdatesToPayload({
      ...updates,
      id: updates.id ?? baseUser.id,
    });

    const updatedProfile = await updateMyProfile(undefined, payload);
    const finalUser = {
      ...updatedProfile,
      avatar: updates.avatar ?? updatedProfile.avatar,
    };
    cacheUserProfile(finalUser);
    set({ authUser: buildAuthUser(finalUser) });
  },

  getAvatarUploadUrl: async (contentType) => {
    return requestAvatarUploadUrl(undefined, { contentType });
  },

  completeAvatarUpload: async (objectPath) => {
    const updatedProfile = await completeAvatarUploadApi(undefined, { objectPath });
    cacheUserProfile(updatedProfile);
    return updatedProfile;
  },

  deleteAvatar: async () => {
    const updatedProfile = await deleteAvatarApi();
    cacheUserProfile(updatedProfile);
    return updatedProfile;
  },

  refreshUserProfile: async () => {
    const { authUser } = get();
    if (!authUser) return;
    const profile = await fetchUserProfile();
    cacheUserProfile(profile);
    set({ authUser: buildAuthUser(profile) });
  },

  issueWalletChallenge: async (address, domain) => {
    return requestAuthChallenge({ address, domain });
  },

  startXConnection: async (xRole, communityId) => {
    const { url } = await startXAuth(undefined, {
      xRole,
      communityId,
      returnTo: getCurrentReturnTo(),
    });
    trackUsageEvent("social_connection_started", {
      provider: "x",
      role: xRole,
      community_id: communityId ?? null,
    });
    return url;
  },

  startDiscordConnection: async (role, communityId, returnTo) => {
    const { url } = await startDiscordAuth(undefined, {
      role,
      communityId,
      returnTo: returnTo ?? getCurrentReturnTo(),
    });
    trackUsageEvent("social_connection_started", {
      provider: "discord",
      role,
      community_id: communityId ?? null,
    });
    return url;
  },

  disconnectXAccount: async () => {
    const updatedProfile = await disconnectX();
    cacheUserProfile(updatedProfile);
    trackUsageEvent("social_connection_disconnected", { provider: "x" });
  },

  disconnectDiscordAccount: async () => {
    const updatedProfile = await disconnectDiscord();
    cacheUserProfile(updatedProfile);
    trackUsageEvent("social_connection_disconnected", { provider: "discord" });
  },
}));
